// eslint-disable-next-line no-unused-vars
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import { useTranslation } from 'react-i18next';
import { consultancyOverviewStyles as sx } from './ConsultancyOverview.styles';

// Map the "color" key produced by useDashboardData → a concrete CSS color
// that the donut + stripes + value text use. Keys come from STATUS_COLOR_MAP
// in useDashboardData.js (success, info, warning, error, primary).
const STATUS_COLOR_HEX = {
  success: '#18a558',
  info:    '#7c5cf0',
  warning: '#e0a106',
  error:   '#d64545',
  primary: '#8b95a5',
};

// Satir basina gosterilen tur rozeti sayisi. Uzerini tek bir "+N" rozeti
// topluyor; siniri kaldirip hepsini basmak satiri tasiriyordu.
// Tur rozetleri KIRPILMIYOR: her satirda hangi turden kac is oldugu
// dogrudan okunabilmeli. Onceden ilk 4 gosterilip kalani "+N tur"
// rozetinde toplaniyordu; hangi turlerin arkada kaldigini gormek icin
// uzerine gelmek gerekiyordu. Rozetler zaten satira siga siga akiyor.

const FALLBACK_COLORS = ['#3a5af0', '#18a558', '#e0a106', '#7c5cf0', '#d64545', '#0ea5e9'];

const STATUS_SUBTITLES = {
  'Yazımda':            'Devam eden çalışmalar',
  'Değerlendirmede':    'İnceleme aşamasında',
  'Onaylandı':          'Onayı tamamlanan dosyalar',
  'Reddedildi':         'Olumsuz sonuçlanan',
  'Kapatıldı':          'Kapatılmış dosyalar',
  'Tamamlandı':         'Tamamlanan çalışmalar',
  'Devralındı':         'Devralınan dosyalar',
  'Ortak Bulundu':      'Ortaklı yürüyen',
  'Diğer':              'Diğer durumlar',
};

// Tip ismine gore tematik renk. Anahtarlar normalize ediliyor (kucuk harf,
// Turkce karakterler arindirilmis) — boylece "TÜBİTAK" / "Tubitak" / "tubitak"
// hepsi ayni renkle eslenir.
const TYPE_COLOR = {
  teknopark:     '#3a5af0', // mavi
  tubitak:       '#e0a106', // amber
  uluslararasi:  '#7c5cf0', // mor
  diger:         '#8b95a5', // nötr
  default:       '#3a5af0',
};

const normalizeTypeKey = (s = '') =>
  String(s)
    .toLocaleLowerCase('tr')
    .replace(/[ıİ]/g, 'i')
    .replace(/[üÜ]/g, 'u')
    .replace(/[öÖ]/g, 'o')
    .replace(/[şŞ]/g, 's')
    .replace(/[çÇ]/g, 'c')
    .replace(/[ğĞ]/g, 'g')
    .replace(/[^a-z0-9]/g, '');

const colorForType = (name) => {
  const k = normalizeTypeKey(name);
  return TYPE_COLOR[k] || TYPE_COLOR.default;
};

// Build SVG arc paths so each segment of the donut is a separate path —
// gives us crisp color borders without overlapping strokes.
const polarToCartesian = (cx, cy, r, angleDeg) => {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};

const arcPath = (cx, cy, rOuter, rInner, startAngle, endAngle) => {
  const largeArc = endAngle - startAngle <= 180 ? 0 : 1;
  const p1 = polarToCartesian(cx, cy, rOuter, endAngle);
  const p2 = polarToCartesian(cx, cy, rOuter, startAngle);
  const p3 = polarToCartesian(cx, cy, rInner, startAngle);
  const p4 = polarToCartesian(cx, cy, rInner, endAngle);
  return [
    `M ${p1.x} ${p1.y}`,
    `A ${rOuter} ${rOuter} 0 ${largeArc} 0 ${p2.x} ${p2.y}`,
    `L ${p3.x} ${p3.y}`,
    `A ${rInner} ${rInner} 0 ${largeArc} 1 ${p4.x} ${p4.y}`,
    'Z',
  ].join(' ');
};

const ConsultancyOverview = ({ groups = [] }) => {
  const { t } = useTranslation();

  // Normalize groups to {label, value, color, subjects[]}
  const segments = useMemo(() => {
    return groups.map((g, idx) => ({
      label: g.statusName,
      value: g.total,
      color: STATUS_COLOR_HEX[g.color] || FALLBACK_COLORS[idx % FALLBACK_COLORS.length],
      subjects: g.subjects || [],
    }));
  }, [groups]);

  const total = useMemo(
    () => segments.reduce((s, g) => s + g.value, 0),
    [segments],
  );

  // Mount animation — donut scales/fades in subtly when segments first appear
  // and whenever the segment composition changes (e.g. filter switch).
  const [donutMounted, setDonutMounted] = useState(false);
  useEffect(() => {
    setDonutMounted(false);
    const id = requestAnimationFrame(() => setDonutMounted(true));
    return () => cancelAnimationFrame(id);
  }, [segments]);

  // SVG donut math — all constants, so they're stable across renders.
  const size = 184;
  const cx = size / 2;
  const cy = size / 2;
  const rOuter = 84;
  const rInner = 58;

  const arcs = useMemo(() => {
    if (total <= 0) return [];
    let angle = 0;
    return segments.map((seg) => {
      const sweep = (seg.value / total) * 360;
      const start = angle;
      // Avoid full-circle sweep degenerating into a closed path: cap at 359.99.
      const end = angle + Math.min(sweep, 359.99);
      angle = end;
      return {
        d: arcPath(cx, cy, rOuter, rInner, start, end),
        color: seg.color,
        label: seg.label,
      };
    });
    // cx/cy/rOuter/rInner are component-scope constants; omitting them from
    // deps is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segments, total]);

  if (segments.length === 0 || total === 0) {
    return (
      <Box sx={sx.card}>
        <Box sx={sx.header}>
          <Box sx={sx.headerLeft}>
            <Box sx={sx.headerIcon}>
              <BusinessCenterIcon sx={{ fontSize: '1.15rem' }} />
            </Box>
            <Box>
              <Typography sx={sx.headerTitle}>{t('dashboard.consultancy.title')}</Typography>
              <Typography sx={sx.headerSubtitle}>Durum ve kategori dağılımı</Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={sx.empty}>{t('dashboard.consultancy.empty')}</Box>
      </Box>
    );
  }

  return (
    <Box sx={sx.card}>
      <Box sx={sx.header}>
        <Box sx={sx.headerLeft}>
          <Box sx={sx.headerIcon}>
            <BusinessCenterIcon sx={{ fontSize: '1.15rem' }} />
          </Box>
          <Box>
            <Typography sx={sx.headerTitle}>{t('dashboard.consultancy.title')}</Typography>
            <Typography sx={sx.headerSubtitle}>Durum ve kategori dağılımı</Typography>
          </Box>
        </Box>

        <Box sx={sx.countBadge}>
          {total} {t('dashboard.consultancy.records')}
        </Box>
      </Box>

      <Box sx={sx.body}>
        {/* Sol: donut + legend */}
        <Box sx={sx.donutWrap}>
          <Box sx={sx.donutBox(donutMounted)}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
              <circle cx={cx} cy={cy} r={rOuter} fill="#f4f5f7" />
              <circle cx={cx} cy={cy} r={rInner} fill="#ffffff" />
              {arcs.map((arc, i) => (
                <path key={`${arc.label}-${i}`} d={arc.d} fill={arc.color} />
              ))}
            </svg>
            <Box sx={sx.donutCenter}>
              <Typography sx={sx.donutCenterValue}>{total}</Typography>
              <Typography sx={sx.donutCenterLabel}>Danışmanlık</Typography>
            </Box>
          </Box>

          <Box sx={sx.legend}>
            {segments.map((seg) => (
              <Box key={seg.label} sx={sx.legendItem}>
                <Box sx={sx.legendSwatch(seg.color)} />
                {seg.label}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Sağ: status list */}
        <Box sx={sx.list}>
          {segments.map((seg, i) => (
            <Box key={seg.label} sx={sx.row(i === segments.length - 1)}>
              <Box sx={sx.stripe(seg.color)} />

              <Box sx={sx.rowTextBox}>
                <Typography sx={sx.rowTitle}>{seg.label}</Typography>
                <Typography sx={sx.rowSubtitle}>
                  {STATUS_SUBTITLES[seg.label] || 'Durum dağılımı'}
                </Typography>
              </Box>

              <Box sx={sx.chipsWrap}>
                {seg.subjects.map((s) => {
                  const tone = colorForType(s.name);
                  return (
                    <Box key={s.name} sx={sx.chip(tone)}>
                      <Box component="span" sx={sx.chipDot(tone)} />
                      <Box component="span" sx={sx.chipName}>{s.name}</Box>
                      <Box component="span" sx={sx.chipCount(tone)}>{s.count}</Box>
                    </Box>
                  );
                })}
              </Box>

              <Typography sx={sx.rowValue(seg.color)}>{seg.value}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ConsultancyOverview;
